import type { UseQueryOptions } from '@core/types'
import type { GameVendor } from '@nimbus/components/Strapi/VendorGames.vue'
import { useCoreConfig, useStrapi, useQuery, useVendors } from '#imports'

/**
 * Normalizes a raw Strapi vendor entry into a GameVendor shape.
 */
export function normalizeStrapiVendor(vendor: GameVendor): GameVendor {
    return {
        name: vendor.name,
        slug: vendor.slug,
        providerSlug: vendor.providerSlug,
        useProvider: !!(vendor.useProviderSlug || vendor.useProvider),
        useProviderSlug: vendor.useProviderSlug,
    }
}

export const useStrapiVendorList = () => {
    const { site } = useCoreConfig()
    const strapi = useStrapi()
    const { data: vendorsData } = useVendors()

    /**
     * Returns a list of vendors.
     * Available vendors from Game Configuration will be cross referenced with vendors defined in Strapi.
     */
    function vendorList(
        options: Pick<UseQueryOptions<any>, 'enabled' | 'lazy' | 'server'> = {}
    ) {
        const { enabled = true, lazy = false, server = false } = options

        const query = useQuery(async () => {
            const { data } = await strapi.GET('/vendorlist', {
                params: {
                    query: {
                        brand: site.brand
                    }
                },
            })

            return data
        }, {
            key: 'strapi:vendorlist',
            lazy,
            server,
            enabled,
        })

        const vendors: GameVendor[] | undefined = query.data.value?.[0]?.vendors

        if (!vendors) return []

        const vendorsFromGameConfig = new Set(vendorsData.value?.map(x => x.slug))

        const strapiGameVendorList: Array<GameVendor> = vendors.map(normalizeStrapiVendor)

        return strapiGameVendorList.filter(vendor => vendorsFromGameConfig.has(vendor.slug))
    }

    return {
        vendorList
    }
}
